import type { AxiosError as ApiError } from "axios"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Button,
  Center,
  Container,
  Flex,
  Heading,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useEffect } from "react"
import { FiArrowLeft } from "react-icons/fi"

import { type ProviderGroupPublic, ProviderGroups } from "@/client"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"
import { Field } from "@/components/ui/field"

interface ProviderGroupEditSearch {
  providerGroupId: string
}

interface ProviderGroupUpdateForm {
  name: string
  description?: string
}

export const Route = createFileRoute("/_layout/provider/providergroups/edit")({
  component: ProviderGroupEdit,
  validateSearch: (search: Record<string, unknown>): ProviderGroupEditSearch => {
    return {
      providerGroupId: (search.providerGroupId as string) || "",
    }
  },
})

function ProviderGroupEdit() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const storedProviderId = sessionStorage.getItem("selectedProviderId")
  const { providerGroupId } = Route.useSearch()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ProviderGroupUpdateForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: { name: "", description: "" },
  })

  const { data: providerGroup, isLoading, error } = useQuery<ProviderGroupPublic>({
    queryKey: ["providerGroup", providerGroupId],
    queryFn: () => ProviderGroups.readProviderGroup({ path: { id: providerGroupId } }),
    enabled: !!providerGroupId,
  })

  useEffect(() => {
    if (providerGroup) {
      reset({
        name: providerGroup.name ?? "",
        description: providerGroup.description ?? "",
      })
    }
  }, [providerGroup, reset])

  const mutation = useMutation({
    mutationFn: (data: ProviderGroupUpdateForm) => {
      if (!providerGroup) throw new Error("Provider group not loaded")
      return ProviderGroups.updateProviderGroup({
        path: { id: providerGroup.id },
        body: { name: data.name, description: data.description || null, providerId: providerGroup.providerId },
      })
    },
    onSuccess: () => {
      showSuccessToast("Group saved.")
      queryClient.invalidateQueries({ queryKey: ["providerGroups"] })
      queryClient.invalidateQueries({ queryKey: ["providerGroup", providerGroupId] })
    },
    onError: (err: ApiError) => handleError(err),
  })

  const backPath = storedProviderId ? `/provider/providergroups/${storedProviderId}` : "/provider/providergroups"

  const onSubmit: SubmitHandler<ProviderGroupUpdateForm> = async (data) => {
    mutation.mutate(data)
  }

  if (!providerGroupId) {
    return (
      <Container maxW="full" pt={12}>
        <Text color="red.500">No provider group ID provided.</Text>
      </Container>
    )
  }

  if (isLoading) {
    return (
      <Container maxW="full" pt={12}>
        <Center mt={16}><Spinner size="lg" /></Center>
      </Container>
    )
  }

  if (error || !providerGroup) {
    return (
      <Container maxW="full" pt={12}>
        <Text color="red.500">Failed to load provider group.</Text>
      </Container>
    )
  }

  return (
    <Container maxW="full" pt={8} pb={12}>
      {/* Back link */}
      <button
        onClick={() => navigate({ to: backPath as never })}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: "0.8rem",
          color: "#718096",
          marginBottom: 16,
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
        }}
      >
        <FiArrowLeft size={13} /> Provider Groups
      </button>

      {/* Group identity header */}
      <Heading size="xl" mb={6}>{providerGroup.name}</Heading>

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={4} align="stretch" maxW="lg">
          <Field required invalid={!!errors.name} errorText={errors.name?.message} label="Name">
            <Input
              {...register("name", { required: "Name is required" })}
              placeholder="Group name"
            />
          </Field>

          <Field invalid={!!errors.description} errorText={errors.description?.message} label="Description">
            <Input
              {...register("description")}
              placeholder="Optional description"
            />
          </Field>
        </VStack>

        <Flex gap={3} mt={6} align="center">
          <Button
            variant="solid"
            type="submit"
            loading={isSubmitting || mutation.isPending}
            disabled={!isDirty && !mutation.isPending}
          >
            Save Changes
          </Button>
          <Button
            variant="ghost"
            colorPalette="gray"
            type="button"
            onClick={() => navigate({ to: backPath as never })}
          >
            Back to List
          </Button>
          {mutation.isSuccess && !isDirty && (
            <Text fontSize="sm" color="green.600">Saved</Text>
          )}
        </Flex>
      </form>
    </Container>
  )
}
